import * as React from 'react'
import axios from 'axios'

import { makeStyles, Avatar, Card, Overflow, OverflowItem, Text } from '@fluentui/react-components'

import { Document16Regular, Attach20Regular } from '@fluentui/react-icons'

import OverflowCounter from './OverflowCounter'

import style from '../styles/email-card'

const useStyles = makeStyles(style)

const formatDate = (dateString) => {
  const emailDate = new Date(dateString)

  return Intl.DateTimeFormat('en-GB', {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric'
  }).format(emailDate)
}

const EmailCard = (props) => {
  const { email, emailIndex, isLastCard, lastMessageElementRef, messageIndex } = props

  const [hoveredCardIndex, setHoveredCardIndex] = React.useState(null)

  const isHovered = hoveredCardIndex === `${messageIndex}-${emailIndex}`
  const attachments = email.attachments.filter(attachment => attachment.type === 'external')

  const classes = useStyles()

  const onDialogHandler = (arg) => {
    const data = JSON.parse(arg.message)

    let to = []
    let subSubject = 'FW: '

    if (data.type === 'reply') {
      to = [data.message.sender.split('<')[1].split('>')[0]]
      subSubject = 'RE: '
    }

    if (data.type === 'replyAll') {
      to = [...to, ...data.message.recipients]
      subSubject = 'RE: '
    }

    const sentDate = new Date(data.message.date).toLocaleString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    })

    const infoHtml =
      `<b>From</b>: ${data.message.sender}<br>
      <b>Sent</b>: ${sentDate}<br>
      <b>To</b>: ${data.message.recipients.join('; ')}<br>
      <b>Subject</b>: ${data.message.subject}<br><br>`

    const subject = subSubject + data.message.subject

    let outlookSafeHtml = data.message.body_html
      .replace(/<\/?(html|head|body|link|meta)[^>]*>/gi, '')
      .replace(/position\s*:\s*fixed/gi, '')
      .replace(/z-index\s*:[^;]+;/gi, '')

    outlookSafeHtml = `<div style="font-family: Arial; font-size: 10pt;">${infoHtml}${outlookSafeHtml}</div>`

    outlookSafeHtml = '<br><br><hr>' + outlookSafeHtml

    if (window.Office.context.mailbox.displayNewMessageFormAsync !== undefined) {
      window.Office.context.mailbox.displayNewMessageFormAsync({
        toRecipients: to,
        subject: subSubject + data.message.subject,
        htmlBody: outlookSafeHtml,
        attachments: data.message.attachments
          .filter(attachment => attachment.type === 'external')
          .map(attachment => ({
            type: 'file',
            name: attachment.filename,
            url: attachment.url
          }))
      },
      function (result) {
        if (result.status === window.Office.AsyncResultStatus.Failed) {
          console.error('Failed to create new message:', result.error.message)
        }
      })
    } else {
      window.Office.context.mailbox.item.to.setAsync(to)

      window.Office.context.mailbox.item.body.setAsync(
        outlookSafeHtml,
        { coercionType: window.Office.CoercionType.Html },
        function (result) {
          if (result.status === window.Office.AsyncResultStatus.Failed) {
            console.error('Failed to insert body text:', result.error.message)
          }
        }
      )

      window.Office.context.mailbox.item.subject.setAsync(
        subject,
        function (result) {
          if (result.status === window.Office.AsyncResultStatus.Failed) {
            console.error('Failed to insert subject text:', result.error.message)
          }
        }
      )

      data.message.attachments
        .forEach(attachment => {
          if (attachment.type !== 'external') return

          window.Office.context.mailbox.item.addFileAttachmentAsync(
            attachment.url,
            attachment.filename
          )
        })
    }
  }

  const openPopup = (email) => {
    window.Office.context.ui.displayDialogAsync(
      window.location.origin + '/popup.html',
      { height: 80, width: 80, displayInIframe: true },
      (asyncResult) => {
        const dialog = asyncResult.value

        if (asyncResult.status === window.Office.AsyncResultStatus.Succeeded) {
          axios
            .get(`${process.env.API_URL}/mail/item/${email.id}`, {
              headers: {
                'X-Api-Key': process.env.API_KEY
              }
            })
            .then((res) => {
              dialog.messageChild(JSON.stringify({ message: res.data.message }))
            })

          dialog.addEventHandler(window.Office.EventType.DialogMessageReceived, (arg) => {
            onDialogHandler(arg)
            dialog.close()
          })
        }
      }
    )
  }

  const extractBodyText = (htmlString) => {
    if (typeof window !== 'undefined' && typeof window.DOMParser !== 'undefined') {
      const parser = new window.DOMParser()
      const doc = parser.parseFromString(htmlString, 'text/html')
      const bodyText = doc.body.textContent || 'No prieview is available.'

      return bodyText
        .split('\n')
        .map(line => line.trim().replace(/\s{2,}/g, ' '))
        .filter((line, index, arr) => {
          return line !== '' || (index > 0 && arr[index - 1] !== '')
        })
        .join('\n')
    }

    return 'No prieview is available.'
  }

  return (
    <Card
      appearance='subtle'
      key={emailIndex}
      ref={isLastCard ? lastMessageElementRef : null}
      className={classes.card}
      onClick={() => openPopup(email)}
      onMouseEnter={() => setHoveredCardIndex(`${messageIndex}-${emailIndex}`)}
      onMouseLeave={() => setHoveredCardIndex(null)}
      as='div'
    >
      <Avatar name={email.from_address.split('<')[0].trim()} color='colorful' size={32} className={classes.avatar} />
      <div className={classes.cardTextContainer}>
        <div className={classes.emailRowContainer}>
          <Text>{email.from_address.split('<')[0].trim() || email.from_address.split('<')[0].trim()}</Text>
          {attachments.length > 0 && (<Attach20Regular />)}
        </div>
        <div className={classes.emailRowContainer}>
          <Text truncate={!isHovered} wrap={isHovered}>{email.subject}</Text>
          <Text size={200} className={classes.emailSubjectDate}>{formatDate(email.date)}</Text>
        </div>
        <Text truncate={!isHovered} wrap={isHovered} style={{ whiteSpace: isHovered ? 'pre-wrap' : 'nowrap', overflowY: isHovered ? 'auto' : 'hidden' }} className={classes.emailBodyPreviewText}>
          {extractBodyText(email.body_html)}
        </Text>
        <Overflow>
          <div className={classes.emailAttachmentsContainer}>
            {attachments
              .map((attachment, attachmentIndex) => (
                <OverflowItem key={attachmentIndex} id={attachmentIndex}>
                  <Card as='div' orientation='horizontal' appearance='outline' size='small' className={classes.attachmentCard}>
                    <Document16Regular style={{ marginRight: '4px' }} />
                    <Text truncate wrap={false} size={200} className={classes.attachmentText}>{attachment.filename}</Text>
                  </Card>
                </OverflowItem>
              ))}
            <OverflowCounter />
          </div>
        </Overflow>
      </div>
    </Card>
  )
}

export default EmailCard
